import React, { useState, useEffect } from 'react';
import { CreditCard, Check, X, ArrowUpDown, ArrowUp, ArrowDown, Edit, Trash2, Send, Search, MailCheck, MailX, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Payments = () => {
 const { selectedBranch } = useApp();
 const { token, user } = useAuth();
 const [invoices, setInvoices] = useState([]);
 const [filteredInvoices, setFilteredInvoices] = useState([]);
 const [searchTerm, setSearchTerm] = useState('');
 const [customers, setCustomers] = useState({});
 const [selectedInvoice, setSelectedInvoice] = useState(null);
 const [paymentMethod, setPaymentMethod] = useState('cash');
 const [amount, setAmount] = useState(0);
 const [isDialogOpen, setIsDialogOpen] = useState(false);
 const [showDeleteDialog, setShowDeleteDialog] = useState(false);
 const [deleteInvoiceId, setDeleteInvoiceId] = useState(null);
 const [showEditDialog, setShowEditDialog] = useState(false);
 const [editingInvoice, setEditingInvoice] = useState(null);
 const [editFormData, setEditFormData] = useState({
 amount: 0,
 due_date: '',
 status: '',
 payment_method: ''
 });
 const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
 const [whatsappInvoice, setWhatsappInvoice] = useState(null);
 const [sendWithPDF, setSendWithPDF] = useState(true);
 const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
 const [currentPage, setCurrentPage] = useState(1);
 const [itemsPerPage, setItemsPerPage] = useState(10);
 const [totalItems, setTotalItems] = useState(0);
 const [totalPages, setTotalPages] = useState(0);
 const [searchInput, setSearchInput] = useState('');
 const [loading, setLoading] = useState(true);
 const [searchTimeout, setSearchTimeoutState] = useState(null);
 const [ownerFilter, setOwnerFilter] = useState('ALL');
 const [owners, setOwners] = useState([]);

 useEffect(() => {
 // 2026-08-08 FIX: AbortController pattern — cancel fetch lama saat dependency berubah.
 const controller = new AbortController();
 fetchData({ signal: controller.signal });
 return () => controller.abort();
 }, [currentPage, itemsPerPage, searchTerm, selectedBranch, ownerFilter]);

 // Fetch owners for filter dropdown
 const fetchOwners = async () => {
 try {
 const authToken = localStorage.getItem('token');
 if (!authToken) return;
 
 // If user is owner role, only show their own owner in filter
 const userRole = user?.role?.toLowerCase();
 if (userRole === 'owner') {
 // For owner role, set filter to their owner_id and don't fetch all owners
 if (user?.owner_id) {
 setOwners([{ id: user.owner_id, name: user.full_name || user.username }]);
 setOwnerFilter(String(user.owner_id));
 }
 return;
 }
 
 const params = new URLSearchParams();
 if (selectedBranch && selectedBranch !== 'all') {
 params.append('branch_id', selectedBranch);
 }
 const response = await axios.get(`${API_URL}/api/v2/owners?${params}`, {
 headers: { Authorization: `Bearer ${authToken}` }
 });
 if (response.data.success) {
 setOwners(response.data.data || []);
 }
 } catch (error) {
 console.error('Error fetching owners:', error);
 }
 };

 useEffect(() => {
 const authToken = localStorage.getItem('token');
 if (authToken && user) {
 const userRole = user?.role?.toLowerCase();
 if (userRole === 'owner' && user?.owner_id) {
 // For owner role, auto-set their owner filter
 setOwners([{ id: user.owner_id, name: user.full_name || user.username }]);
 setOwnerFilter(String(user.owner_id));
 } else {
 fetchOwners();
 setOwnerFilter('ALL');
 }
 }
 }, [selectedBranch, user]);

 // Handle search with debounce
 const handleSearchChange = (value) => {
 if (searchTimeout) clearTimeout(searchTimeout);
 setSearchTimeoutState(setTimeout(() => {
 setSearchTerm(value);
 setCurrentPage(1); // Reset to first page on search
 }, 500));
 };

 // Pagination - now server-side
 const paginatedInvoices = filteredInvoices;
 const startIndex = (currentPage - 1) * itemsPerPage;
 const endIndex = startIndex + paginatedInvoices.length;

 const handleSort = (key) => {
 let direction = 'asc';
 if (sortConfig.key === key && sortConfig.direction === 'asc') {
 direction = 'desc';
 }
 setSortConfig({ key, direction });
 };

 const fetchData = async ({ signal = null } = {}) => {
 try {
 setLoading(true);
 const token = localStorage.getItem('token');
 const headers = { Authorization: `Bearer ${token}` };
 
 // Build query params for server-side pagination - V2 API uses offset/limit
 const offset = (currentPage - 1) * itemsPerPage;
 const params = new URLSearchParams({
 limit: itemsPerPage.toString(),
 offset: offset.toString()
 });
 
 // Add search parameter if exists
 if (searchTerm && searchTerm.trim()) {
 params.append('search', searchTerm.trim());
 }
 
 // Add branch filter if selected
 if (selectedBranch && selectedBranch !== 'all') {
 params.append('branch_id', selectedBranch);
 }
 
 // Add owner filter if selected
 if (ownerFilter && ownerFilter !== 'ALL') {
 params.append('owner_id', ownerFilter);
 }
 
 // Use V2 API endpoint for invoices from MySQL
 const invoicesRes = await axios.get(`${API_URL}/api/v2/invoices?${params.toString()}`, { headers, signal });
 
 // SHOW ALL INVOICES (paid and unpaid) for payment tracking
 const allInvoices = invoicesRes.data.data || [];
 setInvoices(allInvoices);
 setFilteredInvoices(allInvoices);
 
 // Set pagination from server response
 const total = invoicesRes.data.total || 0;
 setTotalItems(total);
 setTotalPages(Math.ceil(total / itemsPerPage));
 } catch (error) {
 // 2026-08-08: silent ignore aborted request
 if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
 return;
 }
 console.error('Error fetching payments:', error);
 toast.error('Gagal memuat data');
 } finally {
 setLoading(false);
 }
 };

 const [resendingInvoiceId, setResendingInvoiceId] = useState(null);
 const handleResendEmail = async (invoice) => {
 if (!invoice?.id) return;
 setResendingInvoiceId(invoice.id);
 try {
 const res = await axios.post(
 `${API_URL}/api/v2/invoices/${invoice.id}/resend-payment-email`,
 {},
 { headers: { Authorization: `Bearer ${token}` } }
 );
 if (res.data?.success) {
 toast.success('Email konfirmasi sedang dikirim ulang');
 setTimeout(() => { fetchData(); }, 3500);
 } else {
 toast.error(res.data?.message || 'Gagal kirim ulang email');
 }
 } catch (e) {
 toast.error(e?.response?.data?.detail || 'Gagal kirim ulang email');
 } finally {
 setResendingInvoiceId(null);
 }
 };

 const handlePayment = async (invoice, method) => {
 try {
 const token = localStorage.getItem('token');
 let response;
 
 if (method === 'cash') {
 setSelectedInvoice(invoice);
 // Use total_amount from V2 API response
 setAmount(invoice.total_amount || invoice.amount || invoice.total || 0);
 setIsDialogOpen(true);
 } else if (method === 'xendit') {
 response = await axios.post(`${API_URL}/api/payments/xendit?invoice_id=${invoice.id}`, {}, {
 headers: { Authorization: `Bearer ${token}` }
 });
 if (response.data.success) {
 window.open(response.data.data.invoice_url, '_blank');
 toast.success('Redirect ke Xendit payment page');
 }
 } else if (method === 'duitku') {
 response = await axios.post(`${API_URL}/api/payments/duitku?invoice_id=${invoice.id}`, {}, {
 headers: { Authorization: `Bearer ${token}` }
 });
 if (response.data.success) {
 window.open(response.data.data.paymentUrl, '_blank');
 toast.success('Redirect ke Duitku payment page');
 }
 }
 } catch (error) {
 toast.error('Gagal memproses pembayaran');
 }
 };

 const confirmCashPayment = async () => {
 try {
 const token = localStorage.getItem('token');
 // Use V2 API endpoint for cash payment
 await axios.post(`${API_URL}/api/v2/payments/cash?invoice_id=${selectedInvoice.id}&amount=${amount}`, {}, {
 headers: { Authorization: `Bearer ${token}` }
 });
 toast.success('Pembayaran tunai berhasil dicatat');
 setIsDialogOpen(false);
 fetchData();
 } catch (error) {
 toast.error('Gagal mencatat pembayaran');
 }
 };

 const handleDeleteInvoice = async () => {
 if (!deleteInvoiceId) return;
 
 try {
 const token = localStorage.getItem('token');
 // Use V2 API for delete invoice
 await axios.delete(`${API_URL}/api/v2/invoices/${deleteInvoiceId}`, {
 headers: { Authorization: `Bearer ${token}` }
 });
 toast.success('Invoice berhasil dihapus');
 setShowDeleteDialog(false);
 setDeleteInvoiceId(null);
 fetchData();
 } catch (error) {
 toast.error('Gagal menghapus invoice');
 console.error(error);
 }
 };

 const handleEditClick = (invoice) => {
 setEditingInvoice(invoice);
 setEditFormData({
 // V2 API uses total_amount
 amount: invoice.total_amount || invoice.amount || invoice.total || 0,
 due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
 status: invoice.status || '',
 payment_method: invoice.payment_method || ''
 });
 setShowEditDialog(true);
 };

 const handleEditSubmit = async () => {
 if (!editingInvoice) return;
 
 try {
 const token = localStorage.getItem('token');
 // V2 API uses total_amount and different status values
 const updateData = {
 total_amount: parseFloat(editFormData.amount),
 status: editFormData.status.toLowerCase(), // V2 uses lowercase: pending, paid, overdue
 payment_method: editFormData.payment_method || null
 };

 // Use V2 API for update invoice
 await axios.put(`${API_URL}/api/v2/invoices/${editingInvoice.id}`, updateData, {
 headers: { Authorization: `Bearer ${token}` }
 });
 
 toast.success('Invoice berhasil diupdate');
 setShowEditDialog(false);
 setEditingInvoice(null);
 fetchData();
 } catch (error) {
 toast.error('Gagal update invoice');
 console.error(error);
 }
 };

 const handleSendWhatsApp = (invoice) => {
 setWhatsappInvoice(invoice);
 setSendWithPDF(true); // Default dengan PDF
 setShowWhatsAppDialog(true);
 };

 const confirmSendWhatsApp = async () => {
 if (!whatsappInvoice) return;
 
 try {
 const token = localStorage.getItem('token');
 await axios.post(
 `${API_URL}/api/v2/invoices/${whatsappInvoice.id}/send-whatsapp?send_pdf=${sendWithPDF}`,
 {},
 { headers: { Authorization: `Bearer ${token}` } }
 );
 
 toast.success(`Invoice berhasil dikirim via WhatsApp${sendWithPDF ? ' dengan PDF' : ''}`);
 setShowWhatsAppDialog(false);
 setWhatsappInvoice(null);
 } catch (error) {
 toast.error(error.response?.data?.detail || 'Gagal mengirim WhatsApp');
 console.error(error);
 }
 };

 return (
 <div className="space-y-4 animate-fade-in p-3 sm:p-4 md:p-6 bg-slate-50 dark:bg-slate-950 min-h-screen w-full max-w-full">
 {/* Header */}
 <div className="flex items-end justify-between gap-2 flex-wrap">
 <div>
 <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Pembayaran</h1>
 <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
 {invoices.length} tagihan belum dibayar
 </p>
 </div>
 </div>

 {/* Payment Methods Info - Outline Compact */}
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
 {/* Tunai */}
 <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 rounded-md flex items-center justify-center bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 flex-shrink-0">
 <span className="text-xs font-semibold">IDR</span>
 </div>
 <div className="min-w-0">
 <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Tunai</h4>
 <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Bayar langsung ke kasir</p>
 </div>
 </div>
 </div>

 {/* Duitku */}
 <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 rounded-md flex items-center justify-center bg-emerald-50 dark:bg-emerald-950/40 overflow-hidden flex-shrink-0">
 <img
 src="https://customer-assets.emergentagent.com/job_kumara-hotspot/artifacts/h1ciffng_duitku.png"
 alt="Duitku"
 className="w-6 h-6 object-contain"
 />
 </div>
 <div className="min-w-0">
 <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Duitku</h4>
 <p className="text-xs text-slate-500 dark:text-slate-400 truncate">VA · QRIS · E-Wallet</p>
 </div>
 </div>
 </div>
 </div>

 {/* Unpaid Invoices */}
 <Card className="border border-slate-200 dark:border-slate-800 shadow-none bg-white dark:bg-slate-900">
 <CardContent className="p-0">
 {/* Show entries and Search */}
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-2 border-b border-slate-100 dark:border-slate-700 gap-2">
 <div className="flex flex-wrap items-center gap-2">
 <div className="flex items-center gap-2">
 <span className="text-xs text-slate-600 dark:text-slate-400">Show</span>
 <select
 value={itemsPerPage}
 onChange={(e) => {
 setItemsPerPage(Number(e.target.value));
 setCurrentPage(1);
 }}
 className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs bg-white dark:bg-slate-700 dark:text-white"
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 <option value={100}>100</option>
 </select>
 <span className="text-xs text-slate-600 dark:text-slate-400">entries</span>
 </div>
 
 {/* Owner Filter - disabled for owner role */}
 <select
 value={ownerFilter}
 onChange={(e) => {
 setOwnerFilter(e.target.value);
 setCurrentPage(1);
 }}
 disabled={user?.role?.toLowerCase() === 'owner'}
 className={`px-3 py-1 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs bg-white ${user?.role?.toLowerCase() === 'owner' ? 'opacity-60 cursor-not-allowed' : ''}`}
 >
 <option value="ALL">Semua Owner</option>
 {owners.map(owner => (
 <option key={owner.id} value={owner.id}>{owner.name}</option>
 ))}
 </select>
 </div>
 
 <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
 {/* Search Input */}
 <div className="relative w-full sm:w-48">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500" />
 <Input
 type="text"
 placeholder="Cari..."
 value={searchInput}
 onChange={(e) => {
 setSearchInput(e.target.value);
 handleSearchChange(e.target.value);
 }}
 className="pl-9 h-8 text-xs border-slate-300 focus:border-indigo-500 w-full dark:border-slate-600"
 />
 </div>
 
 <div className="text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
 {totalItems > 0 ? startIndex + 1 : 0}-{endIndex} / {totalItems}
 </div>
 </div>
 </div>

 <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
 <table className="w-full" style={{ minWidth: '850px' }}>
 <thead className="bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600">
 <tr>
 <th 
 className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
 onClick={() => handleSort('invoice_number')}
 >
 <div className="flex items-center gap-1">
 No. Invoice
 {sortConfig.key === 'invoice_number' ? (
 sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
 ) : (
 <ArrowUpDown className="w-3 h-3 opacity-30" />
 )}
 </div>
 </th>
 <th 
 className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
 onClick={() => handleSort('customer_name')}
 >
 <div className="flex items-center gap-1">
 Pelanggan
 {sortConfig.key === 'customer_name' ? (
 sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
 ) : (
 <ArrowUpDown className="w-3 h-3 opacity-30" />
 )}
 </div>
 </th>
 <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">
 Owner
 </th>
 <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">
 Cabang
 </th>
 <th 
 className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
 onClick={() => handleSort('amount')}
 >
 <div className="flex items-center gap-1">
 Jumlah
 {sortConfig.key === 'amount' ? (
 sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
 ) : (
 <ArrowUpDown className="w-3 h-3 opacity-30" />
 )}
 </div>
 </th>
 <th 
 className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
 onClick={() => handleSort('due_date')}
 >
 <div className="flex items-center gap-1">
 Jatuh Tempo
 {sortConfig.key === 'due_date' ? (
 sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
 ) : (
 <ArrowUpDown className="w-3 h-3 opacity-30" />
 )}
 </div>
 </th>
 <th 
 className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
 onClick={() => handleSort('payment_date')}
 >
 <div className="flex items-center gap-1">
 Tanggal Pembayaran
 {sortConfig.key === 'payment_date' ? (
 sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
 ) : (
 <ArrowUpDown className="w-3 h-3 opacity-30" />
 )}
 </div>
 </th>
 <th className="text-center py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Status</th>
 <th className="text-center py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Aksi</th>
 <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Bayar</th>
 </tr>
 </thead>
 <tbody>
 {paginatedInvoices.map((invoice, index) => {
 const customer = customers[invoice.customer_id];
 const dueDate = new Date(invoice.due_date);
 const isOverdue = dueDate < new Date();
 
 return (
 <tr key={index} className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
 <td className="py-2 px-3 font-mono text-xs text-slate-700 dark:text-slate-300">{invoice.invoice_number}</td>
 <td className="py-2 px-3">
 <div className="font-medium text-xs text-slate-800 dark:text-white">{invoice.customer_name || customer?.name || '-'}</div>
 <div className="text-[10px] text-slate-500 dark:text-slate-400">{invoice.customer_account || invoice.customer_account_number || '-'}</div>
 </td>
 <td className="py-2 px-3">
 <div className="text-xs text-slate-700 dark:text-slate-300">{invoice.owner_name || '-'}</div>
 </td>
 <td className="py-2 px-3">
 <div className="text-xs text-slate-700 dark:text-slate-300">{invoice.branch_name || '-'}</div>
 </td>
 <td className="py-2 px-3 font-semibold text-xs text-slate-800 dark:text-white">
 Rp {(invoice.total_amount || invoice.amount || invoice.total || 0).toLocaleString('id-ID')}
 </td>
 <td className="py-2 px-3">
 <div className="text-xs text-slate-600 dark:text-slate-400">{format(dueDate, 'dd MMM yyyy')}</div>
 {isOverdue && (
 <Badge variant="destructive" className="text-[10px] py-1 px-2 mt-0.5">Jatuh Tempo</Badge>
 )}
 </td>
 <td className="py-2 px-3">
 {invoice.paid_date || invoice.payment_date ? (
 <div className="text-xs text-slate-600 dark:text-slate-400">
 {format(new Date(invoice.paid_date || invoice.payment_date), 'dd MMM yyyy')}
 </div>
 ) : (
 <div className="text-xs text-slate-400 dark:text-slate-500 italic">Belum dibayar</div>
 )}
 </td>
 <td className="py-2 px-3">
 <div className="flex flex-col items-center gap-1">
 {invoice.status === 'paid' || invoice.is_paid ? (
 <Badge className="bg-transparent border border-emerald-500 text-emerald-600 text-[10px] py-1 px-2 dark:text-emerald-400">
 {invoice.payment_method || 'LUNAS'}
 </Badge>
 ) : (
 <Badge className="bg-transparent border border-amber-500 text-amber-600 text-[10px] py-1 px-2 dark:text-amber-400">
 BELUM BAYAR
 </Badge>
 )}
 {/* FIX 2026-02: Email delivery indicator + resend button (only for paid invoices) */}
 {(invoice.status === 'paid' || invoice.is_paid) && invoice.customer_email && (
 <div className="inline-flex items-center gap-1">
 {invoice.email_sent_at ? (
 <div
 data-testid="email-sent-badge"
 className="inline-flex items-center gap-1 text-[9px] text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded px-1.5 py-0.5"
 title={`Email terkirim: ${format(new Date(invoice.email_sent_at), 'dd MMM yyyy HH:mm')} ke ${invoice.email_sent_to || '-'}`}
 >
 <MailCheck className="w-2.5 h-2.5" />
 <span>Email</span>
 </div>
 ) : (
 <div
 data-testid="email-not-sent-badge"
 className="inline-flex items-center gap-1 text-[9px] text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/40 rounded px-1.5 py-0.5"
 title="Email konfirmasi belum terkirim (bayaran lama sebelum feature aktif)"
 >
 <MailX className="w-2.5 h-2.5" />
 <span>-</span>
 </div>
 )}
 <button
 type="button"
 data-testid="resend-email-btn"
 onClick={() => handleResendEmail(invoice)}
 disabled={resendingInvoiceId === invoice.id}
 title={`Kirim ulang email konfirmasi ke ${invoice.customer_email}`}
 className="inline-flex items-center justify-center w-4 h-4 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-40 disabled:cursor-wait transition-colors"
 >
 <RefreshCw className={`w-2.5 h-2.5 ${resendingInvoiceId === invoice.id ? 'animate-spin' : ''}`} />
 </button>
 </div>
 )}
 </div>
 </td>
 <td className="py-2 px-3">
 <div className="flex justify-center gap-1">
 <Button
 size="sm"
 variant="outline"
 className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 border-blue-200 dark:border-blue-700 dark:text-blue-400"
 onClick={() => handleEditClick(invoice)}
 title="Edit Invoice"
 >
 <Edit className="w-3 h-3" />
 </Button>
 <Button
 size="sm"
 variant="outline"
 className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 border-red-200 dark:border-red-700 dark:text-red-400"
 onClick={() => {
 setDeleteInvoiceId(invoice.id);
 setShowDeleteDialog(true);
 }}
 title="Hapus Invoice"
 >
 <Trash2 className="w-3 h-3" />
 </Button>
 </div>
 </td>
 <td className="py-2 px-3">
 {!invoice.is_paid ? (
 <div className="flex space-x-1">
 <Button
 data-testid="pay-cash-btn"
 size="sm"
 variant="outline"
 className="text-emerald-600 hover:bg-emerald-50 border-emerald-200 text-[10px] py-1 px-2 h-auto dark:text-emerald-400 dark:hover:bg-emerald-900/30"
 onClick={() => handlePayment(invoice, 'cash')}
 >
 Tunai
 </Button>
 <Button
 size="sm"
 variant="outline"
 className="text-blue-600 hover:bg-blue-50 border-blue-200 text-[10px] py-1 px-2 h-auto dark:text-blue-400 dark:hover:bg-blue-900/30"
 onClick={() => handlePayment(invoice, 'xendit')}
 >
 Xendit
 </Button>
 <Button
 size="sm"
 variant="outline"
 className="text-purple-600 hover:bg-purple-50 border-purple-200 text-[10px] py-1 px-2 h-auto dark:text-purple-400 dark:hover:bg-purple-900/30"
 onClick={() => handlePayment(invoice, 'duitku')}
 >
 Duitku
 </Button>
 </div>
 ) : (
 <Button
 size="sm"
 variant="outline"
 className="text-green-600 hover:bg-green-50 border-green-200 text-[10px] py-1 px-2 h-auto dark:text-green-400 dark:hover:bg-green-900/30"
 onClick={() => handleSendWhatsApp(invoice)}
 title="Kirim bukti pembayaran via WhatsApp"
 >
 <Send className="w-3 h-3 mr-1" />
 Kirim WA
 </Button>
 )}
 </td>
 </tr>
 );
 })}
 
 {/* Empty State */}
 {paginatedInvoices.length === 0 && (
 <tr>
 <td colSpan="10" className="py-12 text-center">
 <div className="flex flex-col items-center gap-3">
 <Check className="w-12 h-12 text-green-500" />
 <div>
 <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Tidak Ada Invoice yang Perlu Dibayar</h3>
 <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Semua invoice sudah lunas. Selamat! 🎉</p>
 </div>
 </div>
 </td>
 </tr>
 )}
 </tbody>
 </table>
 </div>

 {/* Pagination */}
 <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
 <div className="text-xs text-slate-600 dark:text-slate-400">
 Halaman {currentPage} dari {totalPages} ({totalItems} total)
 </div>
 
 <div className="flex items-center gap-2">
 <Button
 variant="outline"
 size="sm"
 onClick={() => setCurrentPage(1)}
 disabled={currentPage === 1}
 className="px-2 h-8"
 >
 «
 </Button>
 <Button
 variant="outline"
 size="sm"
 onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
 disabled={currentPage === 1}
 className="px-3 h-8"
 >
 ← Prev
 </Button>
 
 <span className="px-3 py-1 bg-indigo-600 text-white rounded text-sm font-medium">
 {currentPage}
 </span>
 
 <Button
 variant="outline"
 size="sm"
 onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
 disabled={currentPage === totalPages}
 className="px-3 h-8"
 >
 Next →
 </Button>
 <Button
 variant="outline"
 size="sm"
 onClick={() => setCurrentPage(totalPages)}
 disabled={currentPage === totalPages}
 className="px-2 h-8"
 >
 »
 </Button>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Cash Payment Dialog */}
 <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Konfirmasi Pembayaran Tunai</DialogTitle>
 </DialogHeader>
 <div className="space-y-4 mt-4">
 <div className="bg-slate-50 p-4 rounded-lg dark:bg-slate-900">
 <div className="text-sm text-slate-500 dark:text-slate-400">Invoice</div>
 <div className="font-mono font-semibold text-slate-800 dark:text-slate-200">{selectedInvoice?.invoice_number}</div>
 </div>
 <div className="space-y-2">
 <Label>Jumlah Pembayaran (Rp)</Label>
 <Input
 type="number"
 value={amount}
 onChange={(e) => setAmount(parseFloat(e.target.value))}
 />
 </div>
 <div className="flex justify-end space-x-2 pt-4">
 <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
 Batal
 </Button>
 <Button
 className="bg-gradient-to-r from-emerald-600 to-emerald-700"
 onClick={confirmCashPayment}
 >
 <Check className="w-4 h-4 mr-2" />
 Konfirmasi Pembayaran
 </Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>

 {/* Delete Confirmation Dialog */}
 <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
 <DialogContent className="max-w-md">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
 <Trash2 className="w-5 h-5" />
 Hapus Invoice
 </DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <p className="text-sm text-slate-600 dark:text-slate-300">
 Apakah Anda yakin ingin menghapus invoice ini? Tindakan ini tidak dapat dibatalkan.
 </p>
 <div className="flex gap-2 justify-end pt-2">
 <Button
 variant="outline"
 onClick={() => {
 setShowDeleteDialog(false);
 setDeleteInvoiceId(null);
 }}
 >
 Batal
 </Button>
 <Button
 className="bg-red-600 hover:bg-red-700"
 onClick={handleDeleteInvoice}
 >
 Hapus
 </Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>

 {/* Edit Invoice Dialog */}
 <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
 <DialogContent className="max-w-md">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
 <Edit className="w-5 h-5" />
 Edit Invoice
 </DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 {editingInvoice && (
 <>
 <div className="bg-slate-50 rounded-lg p-3 mb-4 dark:bg-slate-900">
 <div className="text-xs text-slate-500 dark:text-slate-400">Invoice Number</div>
 <div className="font-semibold text-slate-900 dark:text-slate-100">{editingInvoice.invoice_number}</div>
 <div className="text-xs text-slate-600 mt-1 dark:text-slate-300">{editingInvoice.customer_name}</div>
 </div>

 <div className="space-y-3">
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
 Jumlah (Rp)
 </label>
 <input
 type="number"
 className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600"
 value={editFormData.amount}
 onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})}
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
 Tanggal Jatuh Tempo
 </label>
 <input
 type="date"
 className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600"
 value={editFormData.due_date}
 onChange={(e) => setEditFormData({...editFormData, due_date: e.target.value})}
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
 Status
 </label>
 <select
 className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600"
 value={editFormData.status}
 onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
 >
 <option value="BELUM BAYAR">Belum Bayar</option>
 <option value="LUNAS">Lunas</option>
 </select>
 </div>

 {editFormData.status === 'LUNAS' && (
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
 Metode Pembayaran
 </label>
 <select
 className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600"
 value={editFormData.payment_method}
 onChange={(e) => setEditFormData({...editFormData, payment_method: e.target.value})}
 >
 <option value="">Pilih Metode</option>
 <option value="Cash">Cash / Tunai</option>
 <option value="Xendit">Xendit</option>
 <option value="Duitku">Duitku</option>
 <option value="Transfer">Transfer Bank</option>
 </select>
 </div>
 )}
 </div>

 <div className="flex gap-2 justify-end pt-2 border-t">
 <Button
 variant="outline"
 onClick={() => {
 setShowEditDialog(false);
 setEditingInvoice(null);
 }}
 >
 Batal
 </Button>
 <Button
 className="bg-blue-600 hover:bg-blue-700"
 onClick={handleEditSubmit}
 >
 Simpan
 </Button>
 </div>
 </>
 )}
 </div>
 </DialogContent>
 </Dialog>

 {/* WhatsApp Send Dialog */}
 <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
 <DialogContent className="max-w-md">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2 text-green-600 dark:text-green-400">
 <Send className="w-5 h-5" />
 Kirim Invoice via WhatsApp
 </DialogTitle>
 </DialogHeader>
 {whatsappInvoice && (
 <div className="space-y-4">
 {/* Invoice Info */}
 <div className="bg-slate-50 rounded-lg p-3 dark:bg-slate-900">
 <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
 {whatsappInvoice.invoice_number}
 </div>
 <div className="text-xs text-slate-600 mt-1 dark:text-slate-300">
 Customer: {whatsappInvoice.customer_name}
 </div>
 <div className="text-xs text-slate-600 dark:text-slate-400">
 Amount: Rp {(whatsappInvoice.amount || 0).toLocaleString('id-ID')}
 </div>
 </div>

 {/* PDF Option */}
 <div className="border rounded-lg p-3 space-y-2">
 <label className="flex items-center gap-2 cursor-pointer">
 <input
 type="checkbox"
 checked={sendWithPDF}
 onChange={(e) => setSendWithPDF(e.target.checked)}
 className="w-4 h-4 text-green-600 rounded dark:text-green-400"
 />
 <span className="text-sm font-medium">Attach PDF Invoice</span>
 </label>
 <p className="text-xs text-slate-500 ml-6 dark:text-slate-400">
 File PDF invoice akan dikirim bersama pesan WhatsApp
 </p>
 </div>

 {/* Preview Message */}
 <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
 <p className="text-xs font-semibold text-blue-900 mb-1">
 📱 Preview Pesan:
 </p>
 <p className="text-xs text-blue-800 whitespace-pre-line">
 Konfirmasi pembayaran untuk invoice {whatsappInvoice.invoice_number} akan dikirim ke nomor WhatsApp customer.
 {sendWithPDF && '\n\n📎 Dengan lampiran: PDF Invoice'}
 </p>
 </div>

 {/* Action Buttons */}
 <div className="flex gap-2 justify-end pt-2 border-t">
 <Button
 variant="outline"
 onClick={() => {
 setShowWhatsAppDialog(false);
 setWhatsappInvoice(null);
 }}
 >
 Batal
 </Button>
 <Button
 className="bg-green-600 hover:bg-green-700"
 onClick={confirmSendWhatsApp}
 >
 <Send className="w-4 h-4 mr-2" />
 Kirim WhatsApp
 </Button>
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>
 </div>
 );
};

export default Payments;